// simuladorLogic.js

export function simularCampaña(presupuesto, calidad, targeting, plataforma, producto, estacionalidad = 0) {
  const calidadMap = { 'baja': 0.5, 'media': 1.0, 'alta': 1.8 };
  const targetingMap = { 'muy_amplio': 1.2, 'amplio': 1.0, 'restringido': 0.7 };
  const plataformaMap = { 'instagram': 1.0, 'google': 1.3, 'tiktok': 0.9 };
  const productoMap = {
    'fisico': { precio: 50, conversionBase: 0.05, factorGeneral: 1.0 },
    'electronica': { precio: 200, conversionBase: 0.03, factorGeneral: 0.8 },
    'ebook': { precio: 25, conversionBase: 0.08, factorGeneral: 1.3 }
  };

  const calidadFactor = calidadMap[calidad];
  const targetingFactor = targetingMap[targeting];
  const plataformaFactor = plataformaMap[plataforma];
  const productoData = productoMap[producto];
  const estacionalidadFactor = 1 + (estacionalidad / 100);

  let impresiones = Math.round(presupuesto * 80 * calidadFactor * plataformaFactor);
  impresiones = Math.round(impresiones * productoData.factorGeneral * estacionalidadFactor);

  let ctrBase = 0.02 * calidadFactor * (1 / targetingFactor) * productoData.factorGeneral;
  const ctr = Math.min(ctrBase * 100 * estacionalidadFactor, 15);

  const clics = Math.round(impresiones * (ctr / 100));

  const conversionRate = productoData.conversionBase * calidadFactor * (1 / targetingFactor);
  const ventas = Math.round(clics * conversionRate * estacionalidadFactor);

  const ingresos = ventas * productoData.precio;
  const roas = presupuesto > 0 ? +(ingresos / presupuesto).toFixed(1) : 0;
  const cpc = clics > 0 ? +(presupuesto / clics).toFixed(2) : 0;

  return { impresiones, ctr: +(ctr).toFixed(1), clics, ventas, roas, cpc, precioMedio: productoData.precio };
}

export function generarConfiguracionAleatoria() {
  const calidades = ['baja', 'media', 'alta'];
  const targetings = ['muy_amplio', 'amplio', 'restringido'];
  const plataformas = ['instagram', 'google', 'tiktok'];
  const productos = ['fisico', 'electronica', 'ebook'];
  
  return {
    presupuesto: Math.floor(Math.random() * (2000 - 50 + 1) + 50),
    calidad: calidades[Math.floor(Math.random() * calidades.length)],
    targeting: targetings[Math.floor(Math.random() * targetings.length)],
    plataforma: plataformas[Math.floor(Math.random() * plataformas.length)],
    producto: productos[Math.floor(Math.random() * productos.length)],
    estacionalidad: Math.floor(Math.random() * 81) // 0 a 80%
  };
}

// Genera explicaciones sobre por qué el ROAS tiene un valor concreto
export function generarAssumptions(calidad, targeting, presupuesto, estacionalidad, plataforma, producto, roas, resultados) {
  const explicaciones = [];
  
  // 1. Análisis de calidad del anuncio
  if (calidad === 'baja') {
    explicaciones.push({
      variable: '🎨 Calidad del anuncio',
      impacto: 'negativo',
      descripcion: 'La calidad baja está reduciendo el CTR y las conversiones. Mejorar el creativo o el copy podría multiplicar tus resultados.',
      sugerencia: 'Prueba a subir la calidad a "media" o "alta" para ver el impacto.'
    });
  } else if (calidad === 'alta') {
    explicaciones.push({
      variable: '🎨 Calidad del anuncio',
      impacto: 'positivo',
      descripcion: 'La calidad alta está mejorando significativamente el CTR y la tasa de conversión.',
      sugerencia: 'Mantén esta línea creativa. Prueba diferentes copies para optimizar aún más.'
    });
  }
  
  // 2. Análisis de targeting
  if (targeting === 'muy_amplio') {
    explicaciones.push({
      variable: '🎯 Targeting',
      impacto: 'negativo parcial',
      descripcion: 'El targeting muy amplio genera muchas impresiones, pero la relevancia es baja. Menos clics y conversiones.',
      sugerencia: 'Restringe la audiencia a intereses más concretos para mejorar el ROI.'
    });
  } else if (targeting === 'restringido') {
    explicaciones.push({
      variable: '🎯 Targeting',
      impacto: 'positivo',
      descripcion: 'El targeting restringido está llegando a una audiencia más relevante. La tasa de conversión es más alta.',
      sugerencia: 'Perfecto para presupuestos ajustados. Si escalas, prueba a ampliar ligeramente.'
    });
  }
  
  // 3. Análisis de presupuesto (rendimientos decrecientes)
  if (presupuesto > 1200) {
    explicaciones.push({
      variable: '💰 Presupuesto',
      impacto: 'rendimientos decrecientes',
      descripcion: `Con ${presupuesto}€, el incremento de gasto no está escalando linealmente. A partir de cierto punto, el ROAS tiende a bajar.`,
      sugerencia: 'Prueba un presupuesto intermedio (600-900€) para encontrar el punto óptimo.'
    });
  } else if (presupuesto < 200) {
    explicaciones.push({
      variable: '💰 Presupuesto',
      impacto: 'limitado',
      descripcion: 'El presupuesto bajo limita el alcance. Tienes poco volumen para testear.',
      sugerencia: 'Aumenta el presupuesto mínimo a 300-500€ para obtener datos significativos.'
    });
  }
  
  // 4. Análisis de plataforma
  if (plataforma === 'google') {
    explicaciones.push({
      variable: '🔍 Plataforma (Google Ads)',
      impacto: 'positivo',
      descripcion: 'Google Ads tiene mayor intención de compra, pero el CPC es más alto.',
      sugerencia: 'Ideal para productos concretos. Asegúrate de usar palabras clave específicas.'
    });
  } else if (plataforma === 'tiktok') {
    explicaciones.push({
      variable: '📱 Plataforma (TikTok)',
      impacto: 'alcance viral',
      descripcion: 'TikTok genera muchas impresiones, pero la conversión directa es menor.',
      sugerencia: 'Perfecto para branding y productos virales. No lo uses para ventas directas sin testear.'
    });
  }
  
  // 5. Análisis de estacionalidad
  if (estacionalidad > 40) {
    explicaciones.push({
      variable: '📈 Estacionalidad',
      impacto: 'positivo alto',
      descripcion: `Con +${estacionalidad}% de estacionalidad, la demanda está muy alta. Aprovecha el momento.`,
      sugerencia: 'Incrementa presupuesto durante estos picos. El ROAS se multiplica.'
    });
  } else if (estacionalidad === 0) {
    explicaciones.push({
      variable: '📈 Estacionalidad',
      impacto: 'neutral',
      descripcion: 'No has activado ningún pico estacional.',
      sugerencia: 'Prueba a subir la estacionalidad al 30-50% para simular Black Friday o Navidad.'
    });
  }
  
  // 6. Análisis del producto
  if (producto === 'electronica') {
    explicaciones.push({
      variable: '💻 Tipo de producto (Electrónica)',
      impacto: 'conversión más lenta',
      descripcion: 'La electrónica tiene un precio alto. La decisión de compra es más larga y la conversión baja.',
      sugerencia: 'Necesitas más puntos de contacto (email, retargeting). El ROAS a corto plazo será bajo.'
    });
  } else if (producto === 'ebook') {
    explicaciones.push({
      variable: '📚 Tipo de producto (Ebook)',
      impacto: 'conversión rápida',
      descripcion: 'Los ebooks tienen precio bajo y alta conversión. El ROAS puede ser muy alto.',
      sugerencia: 'Ideal para probar estrategias de marketing. Escala rápido si funciona.'
    });
  }
  
  // 7. Resumen ejecutivo basado en ROAS
  let resumen = '';
  let colorResumen = '';
  
  if (roas < 2) {
    resumen = '⚠️ Tu campaña no es rentable. Revisa calidad, targeting y plataforma.';
    colorResumen = '#ef4444';
  } else if (roas >= 2 && roas < 4) {
    resumen = '✅ Campaña rentable. Puedes escalar gradualmente.';
    colorResumen = '#f59e0b';
  } else if (roas >= 4) {
    resumen = '🎉 ROAS excelente. Escala esta campaña cuanto antes.';
    colorResumen = '#10b981';
  }
  
  return {
    explicaciones,
    resumen,
    colorResumen,
    roasActual: roas
  };
}